import { Component, inject, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import {
	woelbungSymbol,
	WOELBUNG_SYMBOL_VIEWBOX,
	schnittrichtungSymbol,
	SCHNITT_SYMBOL_VIEWBOX,
	SCHNITT_HOEHENLINIEN,
	type WoelbungForm,
} from './woelbung-symbol';
import { raumbildFacetten, RAUMBILD_VIEWBOX, type Facette } from './woelbung-raumbild';
import { neigungFacettenFuerStufe } from './neigung-raumbild';
import { einbindungFacetten } from './einbindung-raumbild';
import { hanganschlussFacetten } from './hanganschluss-raumbild';

interface ReliefTopic {
	title: string;
	description?: string;
	columns: string[];
	rows: string[][];
	source?: string;
	/**
	 * Zeigt hinter der Tabelle ein Schraegbild des Gelaendes. Woher die Form
	 * kommt, haengt am Thema:
	 *   'woelbung'      aus Spalte 1 (vertikal) und Spalte 2 (horizontal),
	 *   'neigung'       aus dem Winkel der Stufe in Spalte 0,
	 *   'einbindung'    aus der Lage in Spalte 0,
	 *   'hanganschluss' aus der Lage in Spalte 0.
	 */
	raumbild?: 'woelbung' | 'neigung' | 'einbindung' | 'hanganschluss';
	/**
	 * Spalte, in der der Wert steht, den das Feld speichert. Ist sie gesetzt,
	 * laesst sich die Zeile antippen und der Wert wandert ins Feld.
	 *
	 * ⚠ Der Wert muss EXAKT einem Listeneintrag des Feldes entsprechen (die
	 * `id` in `assets/json/<Thema>.json`) - sonst stuende hinterher etwas im
	 * Datensatz, das die Liste nicht kennt. `relief-uebernahme.spec.ts` prueft
	 * das. Gesetzt ist die Angabe bei den Tabellen mit Bild; die uebrigen
	 * Relief-Tabellen bleiben reine Erklaerung.
	 */
	uebernahmeSpalte?: number;
}

/** Oeffentlich fuer den Abgleich mit den Listen im Test. */
export type { ReliefTopic };

export const RELIEF_THEMEN: Record<string, ReliefTopic> = {
	Neigungsstufe: {
		title: 'Hangneigungsstufen nach SEA',
		description: 'Das Schrägbild zeigt denselben Geländeausschnitt, nur unterschiedlich stark geneigt. Es ist überhöht: Ein Hang von 1° und einer von 3° sähen sonst gleich aus. Den Winkel selbst nennt die Spalte daneben.',
		columns: ['Bezeichnung', 'Neigung', 'Beispiel'],
		raumbild: 'neigung',
		uebernahmeSpalte: 0,
		rows: [
			['Ebene', '≤ 1°', 'Talsand-, Sandersandgebiete'],
			['schwächer geneigter Flachhang', '> 1–3°, auch Rampe', 'Grundmoränen'],
			['stärker geneigter Flachhang', '> 3–7°', 'Endmoränen'],
			['schwächer geneigter Lehnhang', '> 7–15°', 'kuppige Endmoränen'],
			['stärker geneigter Lehnhang', '> 15–25°', 'Talränder'],
			['schwächer geneigter Steilhang', '> 25–60°', 'Kerbtäler'],
			['stärker geneigter Steilhang', '> 60°', 'Kreide-, Geschiebemergelkliffs'],
		],
	},
	Woelbung: {
		title: 'Wölbungsstufen für Reliefformen',
		description: 'Vertikal ist der Schnitt den Hang hinab, horizontal quer dazu entlang der Höhenlinie; im Kürzel steht die vertikale Wölbung vorn. Konvex heißt aufgewölbt, konkav eingesenkt. Das Schrägbild zeigt die fertige Form, vorn liegt der Hangfuß.',
		columns: ['Kürzel', 'Vertikal', 'Horizontal'],
		raumbild: 'woelbung',
		uebernahmeSpalte: 0,
		rows: [
			['xx', 'konvex', 'konvex'],
			['xg', 'konvex', 'gestreckt'],
			['xv', 'konvex', 'konkav'],
			['gx', 'gestreckt', 'konvex'],
			['gg', 'gestreckt', 'gestreckt'],
			['gv', 'gestreckt', 'konkav'],
			['vx', 'konkav', 'konvex'],
			['vg', 'konkav', 'gestreckt'],
			['vv', 'konkav', 'konkav'],
		],
	},
	Hangrichtung: {
		title: 'Hangrichtung (Exposition)',
		description: 'Die Hangrichtung wird nur ab stärker geneigtem Flachhang (> 3°) berücksichtigt. Sie ist umso wichtiger, je stärker die Neigung ist.',
		columns: ['Richtung', 'Bezeichnung'],
		rows: [
			['N', 'Nord'],
			['NO', 'Nordost'],
			['O', 'Ost'],
			['SO', 'Südost'],
			['S', 'Süd'],
			['SW', 'Südwest'],
			['W', 'West'],
			['NW', 'Nordwest'],
		],
	},
	ReliefformUmgebung: {
		title: 'Einbindung in die Nachbarschaft',
		description: 'Bei Ebenen und Rampen (≤ 3°) wird nach der Grundrißfigur und den Grenzlängenanteilen zu benachbarten höher oder tiefer liegenden Arealen unterschieden. Das Schrägbild zeichnet die Beschreibung daneben nach; die offene Seite zeigt jeweils nach vorn.',
		// „Lage“ statt „Form“: Die Bildspalte heisst Form, und zweimal dasselbe
		// Wort im Kopf einer Tabelle verwirrt mehr, als es erklaert.
		columns: ['Lage', 'Beschreibung'],
		raumbild: 'einbindung',
		uebernahmeSpalte: 0,
		rows: [
			['Weitlage', 'allseitig eben, große Ausdehnung'],
			['Horstlage', 'herausragend, allseitig abfallend, rundlich'],
			['Rückenlage', 'herausragend, allseitig abfallend, länglich'],
			['Riedellage', 'hochliegend, nach drei Seiten frei und abfallend'],
			['Schulterlage', 'hochliegend, nach einer Seite abfallend'],
			['Simslage', 'zwischen zwei Arealen, eine Seite ansteigend, andere abfallend'],
			['Fußlage', 'tiefliegend, nach einer Seite ansteigend'],
			['Nischenlage', 'eingesenkt, nach drei Seiten ansteigend, einseitig offen'],
			['Senkenlage', 'eingesenkt, rundlich, allseitig geschlossen und ansteigend'],
			['Strecksenkenlage', 'eingesenkt, länglich, allseitig geschlossen'],
			['Tallage', 'eingesenkt, langgestreckt, zweiseitig offen'],
		],
	},
	Hanganschluss: {
		title: 'Hanganschluss',
		description: 'Gibt Auskunft, ob der Hang Bestandteil eines Voll- oder Hohlreliefs ist (bei stärker geneigten Hängen > 3°). Der Hang ist in allen vier Schrägbildern derselbe; dazu kommt oben eine Kuppe und unten eine Senke. Oben liegt hinten, der Hangfuß vorn.',
		columns: ['Lage', 'Bezeichnung', 'Oben', 'Unten'],
		raumbild: 'hanganschluss',
		uebernahmeSpalte: 0,
		rows: [
			['B-Lage', 'Böschungslage', 'Ebene', 'Ebene'],
			['V-Lage', 'Vollrelieflage', 'Kuppe', 'Ebene'],
			['H-Lage', 'Hohlrelieflage', 'Ebene', 'Senke'],
			['D-Lage', 'Doppellage', 'Kuppe', 'Senke'],
		],
	},
	Hangteil: {
		title: 'Hangteil',
		description: 'Bei stärker geneigten Hängen mit größerer Höhenspanne wird unterschieden.',
		columns: ['Hangteil', 'Beschreibung'],
		rows: [
			['Oberhang', 'oberer Bereich des Gesamthanges'],
			['Mittelhang', 'mittlerer Bereich des Gesamthanges'],
			['Unterhang', 'unterer Bereich des Gesamthanges'],
		],
	},
	Hoehenspanne: {
		title: 'Höhenspanne des Gesamthanges',
		description: 'Wird bei stärker geneigten Hängen (> 3°) angegeben.',
		columns: ['Begriff', 'Erklärung'],
		rows: [
			['Höhenspanne', 'Senkrechter Abstand zwischen Hangoberkante und Sohle des Kaltluftvorfluters'],
		],
	},
	InnereRauhheit: {
		title: 'Innere Rauhheit',
		description: 'Bezieht sich auf Kleinstformen um und unter 10 m Breite und Höhe.',
		columns: ['Bezeichnung', 'Beschreibung'],
		rows: [
			['glatt', 'ohne innere Rauhheit (z.B. Verlandungsmoore)'],
			['leicht bewegt', 'nahezu Ebene, Höhendifferenz 0,5–2 m'],
			['kleinwellig', 'unregelmäßig geschwungen, 2–5 m'],
			['mittelwellig', 'unregelmäßig geschwungen, 5–15 m'],
			['großwellig', 'unregelmäßig geschwungen, > 15 m'],
			['kupstig', 'eckige Formen, dichte Aufeinanderfolge (Jungdünenfelder), 1–15 m'],
			['küstendünig', 'wie kupstig, in Küstennähe mit litoralen Sedimenten'],
			['strandwalldünig', 'Dünen auf Strandwällen (Reffen) mit Schlenken (Riegen)'],
			['altdünig', 'regelhaft geformte Dünen, „ruhige" Oberfläche, 5–25 m'],
			['kleinkuppig', 'Kuppen ohne Hohlformen, 5–15 m'],
			['mittelkuppig', 'Kuppen ohne Hohlformen, 15–25 m'],
			['großkuppig', 'Kuppen ohne Hohlformen, > 25 m'],
			['bucklig', 'Kuppen und Senken'],
			['kleingieserartig', 'Rücken, Lehnhänge und Strecksenken, 5–15 m'],
			['mittelgieserartig', 'Rücken, Lehnhänge und Strecksenken, 15–25 m'],
			['ungeordnet versteilt', '> 25% Hangversteilungen ohne Horizontalerstreckung'],
			['horizontal gestuft', 'horizontale Hangversteilungen (auch künstliche Terrassen)'],
			['hangfaltig', 'gerundete Hohl-/Vollformen in Gefällrichtung (periglaziär)'],
			['hangfurchig', 'spitze Hohl-/Vollformen in Gefällrichtung (extraperiglaziär)'],
		],
	},
};

@Component({
	standalone: true,
	imports: [CommonModule],
	template: `
		<div class="modal-header">
			<h5 class="modal-title">{{ topic.title }}</h5>
			<button type="button" class="btn-close" aria-label="Schließen" (click)="activeModal.dismiss()"></button>
		</div>
		<div class="modal-body">
			<p *ngIf="topic.description" class="text-muted small mb-2">{{ topic.description }}</p>
			<p *ngIf="uebernehmbar" class="uebernahme-hinweis small mb-2">Zeile antippen, um den Wert zu übernehmen.</p>
			<table class="table table-sm table-bordered relief-table">
				<thead>
					<tr>
						<th *ngFor="let col of topic.columns">
							<svg *ngIf="schnittFuer(col) as schnitt" class="schnitt-symbol" [attr.viewBox]="schnittViewBox" aria-hidden="true">
								<path class="hoehenlinien" [attr.d]="hoehenlinien" />
								<path class="schnitt" [attr.d]="schnitt" />
							</svg>{{ col }}
						</th>
						<th *ngIf="topic.raumbild">Form</th>
					</tr>
				</thead>
				<tbody>
					<tr *ngFor="let row of topic.rows; let zeile = index"
						[class.uebernehmbar]="uebernehmbar"
						[class.gewaehlt]="istGewaehlt(row)"
						[attr.role]="uebernehmbar ? 'button' : null"
						[attr.tabindex]="uebernehmbar ? 0 : null"
						[attr.title]="uebernehmbar ? 'Wert übernehmen: ' + wertDerZeile(row) : null"
						(click)="uebernehmen(row)"
						(keydown.enter)="uebernehmen(row)"
						(keydown.space)="uebernehmen(row); $event.preventDefault()">
						<td *ngFor="let cell of row; let first = first" [class.key-cell]="first">
							<strong *ngIf="first">{{ cell }}</strong>
							<ng-container *ngIf="!first">
								<svg *ngIf="symbolFuer(cell) as linie" class="woelbung-symbol" [attr.viewBox]="symbolViewBox" aria-hidden="true">
									<path [attr.d]="linie" />
								</svg>{{ cell }}
							</ng-container>
						</td>
						<td *ngIf="topic.raumbild" class="raumbild-zelle">
							<svg class="raumbild" [attr.viewBox]="raumbildViewBox" aria-hidden="true">
								<path *ngFor="let facette of raumbilder[zeile]" [attr.d]="facette.d" [attr.fill]="facette.farbe" [attr.stroke]="facette.kante" />
							</svg>
						</td>
					</tr>
				</tbody>
			</table>
		</div>
	`,
	styles: [`
		.relief-table {
			font-size: 0.85rem;
		}
		.relief-table th {
			background: #f8f9fa;
			white-space: nowrap;
			vertical-align: middle;
		}
		.relief-table td {
			vertical-align: middle;
		}
		.key-cell {
			/*
			 * Bewusst KEIN nowrap: „schwächer geneigter Flachhang“ zwingt die
			 * Tabelle sonst breiter als jedes Fenster, und dann scrollt man
			 * waagerecht - genau das will hier niemand.
			 */
			min-width: 5rem;
		}
		.woelbung-symbol {
			width: 24px;
			height: 14px;
			margin-right: 0.4rem;
			vertical-align: -2px;
			overflow: visible;
		}
		.woelbung-symbol path {
			fill: rgba(13, 110, 253, 0.15);
			stroke: currentColor;
			stroke-width: 1.2;
			stroke-linejoin: round;
		}
		.schnitt-symbol {
			width: 22px;
			height: 16px;
			margin-right: 0.35rem;
			vertical-align: -3px;
		}
		.schnitt-symbol .hoehenlinien {
			fill: none;
			stroke: #adb5bd;
			stroke-width: 0.9;
		}
		.schnitt-symbol .schnitt {
			fill: none;
			stroke: currentColor;
			stroke-width: 1.8;
			stroke-linecap: round;
		}
		.raumbild-zelle {
			padding: 0.1rem 0.4rem;
			text-align: center;
			/* Die Bildspalte behaelt ihre Breite, egal wie lang der Text daneben ist. */
			width: 4.5rem;
		}
		.raumbild {
			width: 56px;
			height: 48px;
			display: inline-block;
		}
		.raumbild path {
			stroke-width: 0.35;
		}
		.uebernahme-hinweis {
			color: #0d6efd;
		}
		tr.uebernehmbar {
			cursor: pointer;
		}
		tr.uebernehmbar:hover td,
		tr.uebernehmbar:focus td {
			background: #e7f1ff;
		}
		tr.uebernehmbar:focus {
			outline: 2px solid #0d6efd;
			outline-offset: -2px;
		}
		tr.gewaehlt td {
			background: #fff3cd;
		}
		tr.gewaehlt .key-cell::after {
			content: ' ✓';
			color: #198754;
		}
	`]
})
export class ReliefInfoModalComponent implements OnInit {
	@Input() topicName = 'Neigungsstufe';
	topic!: ReliefTopic;

	readonly symbolViewBox = WOELBUNG_SYMBOL_VIEWBOX;
	readonly schnittViewBox = SCHNITT_SYMBOL_VIEWBOX;
	readonly hoehenlinien = SCHNITT_HOEHENLINIEN;
	readonly raumbildViewBox = RAUMBILD_VIEWBOX;

	/**
	 * Die Schraegbilder, in derselben Reihenfolge wie `topic.rows`. Einmal beim
	 * Oeffnen gerechnet - aus dem Template heraus gerufen wuerde bei jedem
	 * Durchlauf der Aenderungserkennung ein neues Array entstehen, und das
	 * zeichnet die Bilder jedes Mal neu.
	 */
	raumbilder: Facette[][] = [];

	/**
	 * Wert, der gerade im Feld steht. Die Zeile dazu wird hervorgehoben, damit
	 * man sieht, worauf man schaut.
	 */
	@Input() aktuellerWert?: string;

	/**
	 * Ob der Wert von hier aus gesetzt werden darf. Die App schaltet das ab,
	 * wo das Feld gerade gar nicht gilt (die Hangrichtung bei Ebenen) - sonst
	 * schriebe ein Klick in ein Feld, das niemand sieht.
	 */
	@Input() uebernahmeMoeglich = true;

	activeModal = inject(NgbActiveModal);

	ngOnInit(): void {
		this.topic = RELIEF_THEMEN[this.topicName] ?? RELIEF_THEMEN['Neigungsstufe'];
		this.raumbilder = this.topic.raumbild ? this.topic.rows.map(row => this.bildFuerZeile(row)) : [];
	}

	/** Das Schraegbild einer Zeile, je nach Thema aus anderen Spalten gebaut. */
	private bildFuerZeile(row: string[]): Facette[] {
		switch (this.topic.raumbild) {
			case 'woelbung':
				return raumbildFacetten(row[1] as WoelbungForm, row[2] as WoelbungForm);
			case 'neigung':
				return neigungFacettenFuerStufe(row[0]);
			case 'einbindung':
				return einbindungFacetten(row[0]);
			case 'hanganschluss':
				return hanganschlussFacetten(row[0]);
			default:
				return [];
		}
	}

	/** Kleines Querschnitt-Bild vor „konvex“ / „gestreckt“ / „konkav“, sonst nichts. */
	symbolFuer(zellinhalt: string): string | null {
		return woelbungSymbol(zellinhalt);
	}

	/** Schnittrichtung im Spaltenkopf „Vertikal“ / „Horizontal“, sonst nichts. */
	schnittFuer(spaltenkopf: string): string | null {
		return schnittrichtungSymbol(spaltenkopf);
	}

	/** Laesst sich in dieser Tabelle ein Wert per Klick uebernehmen? */
	get uebernehmbar(): boolean {
		return this.topic?.uebernahmeSpalte !== undefined && this.uebernahmeMoeglich;
	}

	/** Der Wert, den die Zeile ins Feld schreibt. */
	wertDerZeile(row: string[]): string | undefined {
		const spalte = this.topic?.uebernahmeSpalte;
		return spalte === undefined ? undefined : row[spalte];
	}

	istGewaehlt(row: string[]): boolean {
		return !!this.aktuellerWert && this.wertDerZeile(row) === this.aktuellerWert;
	}

	/** Klick auf eine Zeile: Wert zurueckgeben und Fenster schliessen. */
	uebernehmen(row: string[]): void {
		if (!this.uebernehmbar) return;
		const wert = this.wertDerZeile(row);
		if (wert === undefined) return;
		this.activeModal.close(wert);
	}
}
